import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

/**
 * PageTitle component sets the page title and meta tags using the provided translation key.
 *
 * @param {Object} props
 * @param {string} props.titleKey - Translation key for title/description
 * @returns {JSX.Element}
 */
const PageTitle = ({ titleKey = 'home' }) => {
  const { t } = useTranslation();

  const title = t(`${titleKey}.title`, 'PathConnect');
  const description = t(`${titleKey}.description`, 'Digitale IT-Lösungen mit Open Source – sicher, unabhängig und flexibel.');
  const image = 'https://pathconnect.eu/assets/images/social-preview.png'; 
  const logo = 'https://pathconnect.eu/assets/images/pathconnect_logo.svg';
  const url = typeof window !== 'undefined' ? window.location.href : 'https://pathconnect.eu';

  return (
    <Helmet>
      <title>{`${title} – PathConnect`}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Strukturierte Daten für Google */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "PathConnect",
          "url": "https://pathconnect.eu",
          "logo": logo,
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+49 1515 4184909", // ⬅️ Optional echte Nummer einsetzen
            "contactType": "customer support",
            "areaServed": "DE",
            "availableLanguage": ["German", "English"]
          }
        })}
      </script>
    </Helmet>
  );
};

PageTitle.propTypes = {
  titleKey: PropTypes.string.isRequired
};

export default PageTitle;